/**
 * Expands bookings into a flat list of occurrences.
 * - For non-recurring bookings: add the booking if it's not marked as an exception.
 * - For recurring bookings: add each occurrence (if not an exception) and
 *   add the primary booking only if it wasn't already included via its recurrences.
 *
 * Note: This function removes the recurringOccurrences property from the resulting booking objects,
 * and adds an isRecurring property to indicate whether the booking is recurring.
 *
 * @param {Array} bookings - Array of booking objects.
 * @returns {Array} List of booking occurrences without recurringOccurrences.
 */
export function expandBookings(bookings) {
  const expandedBookings = [];
  const toValidTime = (value) => {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
  };

  bookings.forEach((booking) => {
    const hasExceptionDates =
      booking.exceptionDates && booking.exceptionDates.length > 0;
    // Calculate the primary booking time.
    const primaryTime = toValidTime(booking.time);

    // Check if the primary booking time is marked as an exception.
    const primaryIsException = hasExceptionDates && primaryTime !== null
      ? booking.exceptionDates.some(
          (exc) => new Date(exc).getTime() === primaryTime
        )
      : false;

    // Create a clean booking object by removing recurringOccurrences.
    const { recurringOccurrences, ...cleanBooking } = booking;

    // For non-recurring bookings: add the booking if it's not an exception.
    if (!booking.recurringOccurrences || booking.recurringOccurrences.length === 0) {
      if (!primaryIsException && primaryTime !== null) {
        expandedBookings.push({
          ...cleanBooking,
          isRecurring: false,
        });
      }
    } else {
      // For recurring bookings, mark occurrences as recurring.
      let primaryOccurrenceIncluded = false;

      // Process each recurring occurrence.
      booking.recurringOccurrences.forEach((occurrence) => {
        const occDate = new Date(occurrence);
        const occTime = occDate.getTime();
        if (Number.isNaN(occTime)) return;
        // Check if this occurrence is an exception.
        const occIsException = hasExceptionDates
          ? booking.exceptionDates.some(
              (exc) => new Date(exc).getTime() === occTime
            )
          : false;
        if (occIsException) return;

        // Add the occurrence as a separate booking object with the updated time.
        expandedBookings.push({
          ...cleanBooking,
          time: occurrence,
          isRecurring: true,
        });

        // Mark the primary occurrence as included if it matches the primary time.
        if (primaryTime !== null && occTime === primaryTime) {
          primaryOccurrenceIncluded = true;
        }
      });

      // If the primary booking time wasn't already added, add it now.
      if (!primaryIsException && !primaryOccurrenceIncluded && primaryTime !== null) {
        expandedBookings.push({
          ...cleanBooking,
          time: booking.time,
          isRecurring: true,
        });
      }
    }
  });

  return expandedBookings;
}
